import BlogLink from 'client/components/core/Sidebar/components/Settings/components/BlogLink'
import { WebsiteLink } from 'client/components/core/Sidebar/components/Settings/components/website-link'
import { MenuLinkTypeEnum } from 'common/components/entities/Menu/enums/MenuLinkTypeEnum'
import { MenuTargetEnum } from 'common/components/entities/Menu/enums/MenuTargetEnum'
import { BaseMenuItemInterface } from 'common/components/entities/Menu/types/MenuInterface'
import { isBlogReplacementPlaceholderValue } from 'common/utils/blogReplacementsUtils'
import { isWebsitePageUrlPlaceholderValue } from 'common/utils/website-replacements-utils'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { menuLinkTypeLocoKeys } from 'src/editor/shared/settings/menu-settings/constants/menu-settings-consts'
import { detectLinkType } from 'src/editor/shared/settings/menu-settings/utils/detect-link-type'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import { Input } from 'src/editor/shared/ui-lib/input'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { MenuItemTitle } from '../menu-item-title'
import { MenuItemWarning } from '../menu-item-warning'

interface BaseMenuItemProps {
  item: BaseMenuItemInterface
  number: number
  updateText: (text: string) => void
  updateLink: (link: string) => void
  updateTarget: (checked: boolean) => void
  remove: () => void
  isAllowedBlogLinks: boolean
  isAllowedWebsiteLinks: boolean
  isMoveDownAvailable?: boolean
  isMoveUpAvailable?: boolean
  sortUp?: () => void
  sortDown?: () => void
  warningText?: string
}

export const BaseMenuItem = ({
  item,
  number,
  isMoveUpAvailable,
  isMoveDownAvailable,
  sortUp,
  sortDown,
  updateText,
  updateLink,
  remove,
  isAllowedBlogLinks,
  isAllowedWebsiteLinks,
  updateTarget,
  warningText,
}: BaseMenuItemProps) => {
  const { t } = useTranslation()
  const { text, link, target } = item

  const [selectedLinkType, setSelectedLinkType] = useState(
    detectLinkType(item, isAllowedBlogLinks, isAllowedWebsiteLinks),
  )

  useEffect(() => {
    setSelectedLinkType(
      detectLinkType(item, isAllowedBlogLinks, isAllowedWebsiteLinks),
    )
  }, [item.link])

  const onChangeOption = (option: OptionSingleValueType<MenuLinkTypeEnum>) => {
    if (option) {
      if (
        (isBlogReplacementPlaceholderValue(item.link) ||
          isWebsitePageUrlPlaceholderValue(item.link)) &&
        option.value === MenuLinkTypeEnum.link_type_custom_url
      ) {
        updateLink('')
      }
      setSelectedLinkType(option.value)
    }
  }

  const options = useMemo(() => {
    const resOptions = [
      {
        value: MenuLinkTypeEnum.link_type_custom_url,
        label: t(menuLinkTypeLocoKeys[MenuLinkTypeEnum.link_type_custom_url]),
      },
    ]
    if (isAllowedBlogLinks) {
      resOptions.push({
        value: MenuLinkTypeEnum.link_type_blog_page,
        label: t(menuLinkTypeLocoKeys[MenuLinkTypeEnum.link_type_blog_page]),
      })
    } else if (isAllowedWebsiteLinks) {
      resOptions.push({
        value: MenuLinkTypeEnum.link_type_website_page,
        label: t(menuLinkTypeLocoKeys[MenuLinkTypeEnum.link_type_website_page]),
      })
    }
    return resOptions
  }, [t, isAllowedBlogLinks, isAllowedWebsiteLinks])

  return (
    <div className="flex w-full flex-col gap-2.5">
      <MenuItemTitle
        number={number}
        isMoveDownAvailable={isMoveDownAvailable}
        isMoveUpAvailable={isMoveUpAvailable}
        sortUp={sortUp}
        sortDown={sortDown}
        text={text}
        updateText={updateText}
        remove={remove}
      />
      {(isAllowedBlogLinks || isAllowedWebsiteLinks) && (
        <SelectNew
          withoutWrapper
          onChange={onChangeOption}
          options={options}
          selectedOption={{
            value: selectedLinkType,
            label: t(menuLinkTypeLocoKeys[selectedLinkType]),
          }}
        />
      )}

      {selectedLinkType === MenuLinkTypeEnum.link_type_blog_page && (
        <BlogLink blogLink={link} update={updateLink} />
      )}
      {selectedLinkType === MenuLinkTypeEnum.link_type_website_page && (
        <WebsiteLink link={link} update={updateLink} />
      )}
      {selectedLinkType === MenuLinkTypeEnum.link_type_custom_url && (
        <Input
          value={link}
          onChange={updateLink}
          placeholder={t('entity_settings.menu.item.link_placeholder')}
        />
      )}
      {warningText && <MenuItemWarning>{warningText}</MenuItemWarning>}
      <CheckboxNew
        label={'entity_settings.menu.item.target_label'}
        onChange={updateTarget}
        checked={target === MenuTargetEnum._blank}
      />
    </div>
  )
}
